import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeSimple, ArrowLeft, PaperPlaneTilt, SpinnerGap, CheckCircle } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 flex items-center justify-center py-12 px-4 transition-colors duration-300" data-testid="forgot-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-neutral-900 dark:text-white">Forgot Password?</h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-2">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-success">
              <div className="w-20 h-20 mx-auto bg-emerald-500 rounded-2xl flex items-center justify-center border-2 border-black shadow-[3px_3px_0px_#000]">
                <CheckCircle size={40} weight="bold" className="text-white" />
              </div>
              <h2 className="text-xl font-black text-neutral-900 dark:text-white">Check your inbox</h2>
              <p className="text-neutral-600 dark:text-neutral-400">
                If an account exists for <strong>{email}</strong>, a password reset link is on its way.
                The link expires in 1 hour.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-[#2B4C6F] font-bold hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft size={18} weight="bold" /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Email Address</Label>
                <div className="relative">
                  <EnvelopeSimple size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl focus:ring-4 focus:ring-[#2B4C6F]/20 dark:bg-neutral-700 dark:text-white"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm font-bold text-red-600" data-testid="forgot-password-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-16 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                data-testid="send-reset-link-btn"
              >
                {loading ? (
                  <><SpinnerGap size={24} weight="bold" className="animate-spin" /> Sending...</>
                ) : (
                  <><PaperPlaneTilt size={24} weight="bold" /> Send Reset Link</>
                )}
              </button>

              <Link
                to="/login"
                className="w-full h-12 flex items-center justify-center gap-2 text-neutral-600 dark:text-neutral-400 font-bold hover:text-neutral-900 dark:hover:text-white transition-colors"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft size={20} weight="bold" /> Back to login
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
